/**
 * TPRA Dashboard Page
 * Following existing page pattern from third-party-risk/pages/home/index.tsx
 */

import { AGENT_TYPES, UploadDialogState } from "@/modules/agent/types";
import UploadDialog from "@/modules/agent/components/upload-dialog";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ROUTES } from "@/shared/constants/routes";
import { Input } from "@nextui-org/react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dashboard from "../../components/Dashboard";

export default function TPRADashboardPage() {
  const navigate = useNavigate();

  // Modal state for New Assessment
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Handle opening the modal
  const handleNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    });
  };

  // Search handler - follows RiskHeatmap pattern
  const handleSearch = useCallback(() => {
    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) {
      navigate(
        `/${ROUTES.THIRD_PARTY_VENDORS_V2}?search=${encodeURIComponent(trimmedSearch)}`
      );
    }
  }, [searchTerm, navigate]);

  // Handle Enter key press
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        {/* Top Bar: Breadcrumb + Search + New Assessment Button */}
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[
                { title: "Third-Party Risk", href: `/${ROUTES.THIRD_PARTY_HOME}` },
                { title: "Home", href: `/${ROUTES.THIRD_PARTY_HOME}` },
              ]}
            />
          </div>
          <div className="flex items-center gap-3">
            {/* Search Input */}
            <Input
              className="w-[288px] h-7"
              classNames={{
                base: "!h-7 !min-h-7",
                mainWrapper: "!h-7 !min-h-7",
                inputWrapper: "!h-7 !min-h-7 bg-[#F8FAFC] border-1 border-[#E2E8F0] rounded-[6px] px-2 py-0 gap-2 hover:bg-[#F8FAFC] group-data-[focus=true]:bg-[#F8FAFC] data-[hover=true]:bg-[#F8FAFC]",
                input: "font-['Inter'] font-normal text-[12px] leading-[16px] text-[#62748E] placeholder:text-[#62748E]",
                innerWrapper: "gap-2",
              }}
              startContent={
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="7" cy="7" r="5.5" stroke="#62748E" strokeWidth="1"/>
                  <line x1="11.0607" y1="11" x2="14" y2="13.9393" stroke="#62748E" strokeWidth="1" strokeLinecap="round"/>
                </svg>
              }
              placeholder="Search by third-party or product name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            {/* New Assessment Button */}
            <ZButton
              color="primary"
              className="text-xs font-medium h-7 px-3"
              onClick={handleNewAssessment}
            >
              New Assessment
            </ZButton>
          </div>
        </div>

        {/* Main Content */}
        <div className="h-full w-full overflow-hidden rounded-lg flex flex-col ">
          <div className="flex-1 flex flex-col overflow-auto px-6 py-6 bg-[#F8FAFC]">
            <Dashboard />
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <UploadDialog data={uploadDialogState} setIsOpen={setUploadDialogState} />
    </ErrorBoundary>
  );
}
